/* Admin — Companion Inbox panel (Feb-2026).
 *
 * A dead-simple parent ↔ Companion mail client. No SMTP anywhere.
 *   Left  — thread list (inbound + outbound, newest first).
 *   Right — selected thread transcript + reply composer.
 *
 * Endpoints:
 *   GET  /admin/companion-messages?direction&only_unread
 *   GET  /admin/companion-messages/thread/{thread_id}
 *   POST /admin/companion-messages/send
 *   POST /admin/companion-messages/{id}/read
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Send, RefreshCw, Inbox, ArrowUpRight, Mail, Reply as ReplyIcon,
} from 'lucide-react';

const API  = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

export default function CompanionInboxPanel() {
  const [messages, setMessages] = useState([]);
  const [counts,   setCounts]   = useState({});
  const [filter,   setFilter]   = useState('all');   // all | inbound | outbound | unread
  const [thread,   setThread]   = useState(null);    // { thread_id, messages: [...] }
  const [loading,  setLoading]  = useState(false);
  const [busy,     setBusy]     = useState(false);
  // Compose state
  const [toUser,   setToUser]   = useState('reader-admin');
  const [subject,  setSubject]  = useState('');
  const [bodyText, setBodyText] = useState('');
  const [replyTo,  setReplyTo]  = useState(null);    // { id, thread_id, subject, from_user }

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (filter === 'inbound' || filter === 'outbound') params.direction = filter;
      if (filter === 'unread') params.only_unread = true;
      const r = await http.get('/admin/companion-messages', { params });
      setMessages(r.data?.messages || []);
      setCounts(r.data?.counts || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Load failed.');
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { refresh(); }, [refresh]);

  const openThread = async (m) => {
    setBusy(true);
    try {
      const r = await http.get(`/admin/companion-messages/thread/${m.thread_id}`);
      setThread(r.data);
      // Mark any inbound rows in the thread as read.
      const unread = (r.data?.messages || []).filter(
        (x) => x.direction === 'inbound' && !x.read_at,
      );
      await Promise.all(unread.map((x) =>
        http.post(`/admin/companion-messages/${x.id}/read`).catch(() => {})));
      if (unread.length) refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Open failed.');
    } finally { setBusy(false); }
  };

  const send = async () => {
    if (!subject.trim() || !bodyText.trim()) {
      toast.error('Subject + body are required.');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        to_user:  replyTo?.from_user || toUser,
        subject:  subject.trim(),
        body:     bodyText,
        thread_id:   replyTo?.thread_id,
        in_reply_to: replyTo?.id,
      };
      await http.post('/admin/companion-messages/send', payload);
      toast.success('Queued for the Companion outbox.');
      setSubject(''); setBodyText(''); setReplyTo(null);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed.');
    } finally { setBusy(false); }
  };

  // Group messages by thread for the left list.
  const grouped = useMemo(() => {
    const m = new Map();
    for (const row of messages) {
      const g = m.get(row.thread_id) || { latest: row, count: 0, unread: 0 };
      g.count += 1;
      if (row.direction === 'inbound' && !row.read_at) g.unread += 1;
      if ((row.created_at || '') > (g.latest?.created_at || '')) g.latest = row;
      m.set(row.thread_id, g);
    }
    return Array.from(m.values()).sort(
      (a, b) => (b.latest.created_at || '').localeCompare(a.latest.created_at || ''),
    );
  }, [messages]);

  return (
    <section className="grid grid-cols-1 gap-4 lg:grid-cols-5"
             data-testid="admin-companion-inbox">
      {/* ── list ─────────────────────────────────────────── */}
      <div className="rounded-md border border-border bg-card lg:col-span-2">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <div>
            <div className="inline-flex items-center gap-1 text-[13px] font-semibold">
              <Mail className="h-3.5 w-3.5" /> Companion inbox
            </div>
            <div className="text-[10.5px] text-muted-foreground">
              inbound unread {counts.inbound_unread || 0} · outbound pending {counts.outbound_pending || 0}
            </div>
          </div>
          <button onClick={refresh}
                  data-testid="cimb-refresh-btn"
                  className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] hover:bg-secondary">
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Refresh
          </button>
        </div>
        <div className="flex items-center gap-1 border-b border-border px-3 py-1.5">
          {['all', 'inbound', 'outbound', 'unread'].map((f) => (
            <button key={f}
                    onClick={() => setFilter(f)}
                    data-testid={`cimb-filter-${f}`}
                    className={`rounded-full border px-2 py-0.5 text-[10.5px] uppercase tracking-wider ${
                      filter === f
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-background hover:bg-secondary'}`}>
              {f}
            </button>
          ))}
        </div>
        <ul className="max-h-[520px] divide-y divide-border overflow-y-auto">
          {grouped.length === 0 && (
            <li className="px-3 py-6 text-center text-[11.5px] text-muted-foreground">No messages.</li>
          )}
          {grouped.map((g) => (
            <li key={g.latest.thread_id}
                onClick={() => openThread(g.latest)}
                data-testid={`cimb-thread-${g.latest.thread_id}`}
                className={`cursor-pointer px-3 py-2 hover:bg-secondary/60 ${
                  thread?.thread_id === g.latest.thread_id ? 'bg-primary/10' : ''}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  {g.latest.direction === 'inbound'
                    ? <Inbox className="h-3 w-3 text-sky-700" />
                    : <ArrowUpRight className="h-3 w-3 text-emerald-700" />}
                  <span className="line-clamp-1 text-[12px] font-medium">{g.latest.subject}</span>
                </div>
                {g.unread > 0 && (
                  <span className="rounded-full bg-oxblood-600 px-1.5 py-0.5 text-[9.5px] font-semibold text-white">
                    {g.unread}
                  </span>
                )}
              </div>
              <div className="text-[10.5px] text-muted-foreground">
                {g.latest.from_user} → {g.latest.to_user} · {(g.latest.created_at || '').slice(0, 16).replace('T', ' ')} · {g.count} msg
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* ── thread + composer ───────────────────────────── */}
      <div className="rounded-md border border-border bg-card p-3 lg:col-span-3"
           data-testid="cimb-thread-panel">
        {thread ? (
          <>
            <div className="mb-2 border-b border-border pb-2">
              <div className="text-[13px] font-semibold">{thread.messages[0]?.subject}</div>
              <div className="text-[10.5px] text-muted-foreground">
                thread {thread.thread_id} · {thread.messages.length} message{thread.messages.length === 1 ? '' : 's'}
              </div>
            </div>
            <ul className="max-h-[300px] space-y-2 overflow-y-auto pr-1">
              {thread.messages.map((m) => (
                <li key={m.id}
                    data-testid={`cimb-msg-${m.id}`}
                    className={`rounded-md border p-2 text-[12px] ${
                      m.direction === 'inbound'
                        ? 'border-sky-500/30 bg-sky-500/5'
                        : 'border-emerald-500/30 bg-emerald-500/5'}`}>
                  <div className="mb-1 flex items-center justify-between text-[10.5px] text-muted-foreground">
                    <span><b className="text-foreground">{m.from_user}</b> → {m.to_user}</span>
                    <span>
                      {(m.created_at || '').slice(0, 16).replace('T', ' ')}
                      {m.direction === 'outbound' && (
                        m.delivered_at
                          ? <span className="ml-1 text-emerald-700">· delivered</span>
                          : <span className="ml-1 text-amber-700">· pending pickup</span>
                      )}
                    </span>
                  </div>
                  <div className="whitespace-pre-wrap">{m.body}</div>
                  {m.direction === 'inbound' && (
                    <button
                      onClick={() => { setReplyTo(m); setSubject(m.subject.startsWith('Re: ') ? m.subject : `Re: ${m.subject}`); }}
                      data-testid={`cimb-reply-${m.id}`}
                      className="mt-1 inline-flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[10.5px] hover:bg-secondary">
                      <ReplyIcon className="h-2.5 w-2.5" /> Reply
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </>
        ) : (
          <div className="mb-3 text-[12px] text-muted-foreground">
            Select a thread on the left, or start a fresh conversation below.
          </div>
        )}

        <div className="mt-3 rounded-md border border-dashed border-border p-3">
          {replyTo && (
            <div className="mb-2 flex items-center justify-between rounded-sm bg-secondary/50 px-2 py-1 text-[10.5px]">
              <span>Replying to <b>{replyTo.from_user}</b> · thread {replyTo.thread_id}</span>
              <button onClick={() => { setReplyTo(null); setSubject(''); }}
                      data-testid="cimb-clear-reply"
                      className="text-muted-foreground hover:text-foreground">Clear</button>
            </div>
          )}
          <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
            <input
              value={toUser}
              onChange={(e) => setToUser(e.target.value)}
              disabled={!!replyTo}
              placeholder="To (reader-admin, author id, …)"
              data-testid="cimb-to"
              className="rounded-md border border-border bg-background px-2 py-1.5 text-[12px]" />
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              data-testid="cimb-subject"
              className="md:col-span-2 rounded-md border border-border bg-background px-2 py-1.5 text-[12px]" />
          </div>
          <textarea
            value={bodyText}
            onChange={(e) => setBodyText(e.target.value)}
            rows={5}
            placeholder="Body — plain text or markdown-lite. Delivered to the Companion the next time it polls the outbox."
            data-testid="cimb-body"
            className="mt-2 w-full rounded-md border border-border bg-background px-2 py-1.5 text-[12px]" />
          <div className="mt-2 flex justify-end">
            <button onClick={send} disabled={busy}
                    data-testid="cimb-send-btn"
                    className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Send
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
